/**
 * Feelings someone invents themselves.
 *
 * The built-in styles are a mode, a tempo range, some chords and a palette.
 * There is nothing privileged about them, so a person who has tuned a piece
 * they like should be able to keep it as a style and roll it again rather than
 * only keeping that one track.
 *
 * Stored in the browser and nowhere else, which is the same promise the rest of
 * the page makes: there is no server here, so there is nothing to store them on
 * even if that were wanted.
 */

import type { Mood } from "../src/randomise.js";

const KEY = "hum.feelings";

/** Guards every storage call. It throws outright in some privacy modes, and a
 *  saved style is not worth taking the page down for. */
function safely<T>(action: () => T, fallback: T): T {
  try {
    return action();
  } catch {
    return fallback;
  }
}

export function loadFeelings(): Mood[] {
  const raw = safely(() => localStorage.getItem(KEY), null);
  if (!raw) return [];
  const parsed = safely(() => JSON.parse(raw) as unknown, null);
  if (!Array.isArray(parsed)) return [];
  // Anything without a name and a mode cannot be rolled, and quietly keeping it
  // would mean a chip that produces an error when pressed.
  return parsed.filter(
    (m): m is Mood => typeof m === "object" && m !== null
      && typeof (m as Mood).name === "string" && typeof (m as Mood).mode === "string",
  );
}

function write(list: Mood[]): void {
  safely(() => localStorage.setItem(KEY, JSON.stringify(list)), undefined);
}

/** Saving under a name that already exists replaces it, which is what someone
 *  tweaking their own style expects rather than ending up with two chips. */
export function saveFeeling(mood: Mood): Mood[] {
  const list = loadFeelings().filter((m) => m.name !== mood.name);
  list.push(mood);
  write(list);
  return list;
}

export function deleteFeeling(name: string): Mood[] {
  const list = loadFeelings().filter((m) => m.name !== name);
  write(list);
  return list;
}

/**
 * A name that is safe to show and cannot collide with a built-in.
 *
 * Trimmed, capped, and stripped of anything that is not a letter, number,
 * space or dash. The chips are built with textContent so markup was never
 * going to execute, but a name of pure whitespace makes an invisible button.
 */
export function cleanName(input: string, taken: string[]): string | null {
  const name = input.trim().replace(/[^\p{L}\p{N} \-']/gu, "").slice(0, 24).trim();
  if (!name) return null;
  if (taken.includes(name.toLowerCase())) return null;
  return name;
}
